import {memo, useCallback, useEffect, useId, useMemo, useRef, type ComponentProps, type Ref} from 'react'
import {announce} from '@github-ui/aria-live'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {useSidebarPageLayoutActions} from '@github-ui/layout-primitives/SidebarPageLayout'
import {Sidebar} from '@github-ui/layout-primitives/Sidebar'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SoftNavOnlyLink as Link, useSearchParams} from '@github-ui/react-router'
import {PinIcon, PinSlashIcon, SortDescIcon, HomeIcon, WorkflowIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, asSlot, Button, IconButton, Label, Spinner} from '@primer/react'
import {workflowDisplayName, type Workflow} from '../views/models'
import {removeWorkflowRunsQueryAndPageSearchParams} from '../queries/workflow-runs/workflow-runs-query'
import {SidebarNavItem} from './SidebarNavItem'
import {useWorkflowPinning} from '../hooks/use-workflow-pinning'
import type {WorkflowSidebarState} from '../hooks/use-workflow-sidebar-state'
import {
  isLabWorkflowPath,
  workflowFilePathFromRoute,
  workflowRouteHref,
  workflowRouteMatchesPath,
} from '../workflow-path'
import styles from './WorkflowSidebar.module.css'
import {useActionsAnalytics} from '../hooks/use-actions-analytics'

export type WorkflowSidebarSelection =
  | {type: 'all-workflows'}
  | {type: 'workflow'; filename: string}
  | {type: 'caches'}
  | {type: 'runners'}

interface WorkflowSidebarProps {
  state: WorkflowSidebarState
  pinnedWorkflowIds: number[]
  allowPinning: boolean
  softNavigationEnabled: boolean
  owner: string
  repo: string
  selection: WorkflowSidebarSelection
  showOnlyRequiredWorkflows: boolean
  refreshWorkflows: () => void
}

export function WorkflowSidebar({
  state,
  pinnedWorkflowIds: initialPinnedIds,
  allowPinning,
  softNavigationEnabled,
  owner,
  repo,
  selection,
  showOnlyRequiredWorkflows,
  refreshWorkflows,
}: WorkflowSidebarProps) {
  const track = useActionsAnalytics()
  const {isExpanded} = useCollapsibleSidebarContext()
  const searchAndSortEnabled = isFeatureEnabled('actions_workflow_sidebar_search_and_sort')
  const [searchParams] = useSearchParams()
  const search =
    selection.type === 'caches' || selection.type === 'runners'
      ? ''
      : removeWorkflowRunsQueryAndPageSearchParams(searchParams).toString()
  const qs = search ? `?${search}` : ''
  const allWorkflowsHref = `/${owner}/${repo}/actions${qs}`
  const allWorkflowsLinkId = useId()
  const firstWorkflowLinkId = useId()
  const selectedWorkflowRef = useRef<HTMLAnchorElement>(null)

  const {
    workflows,
    searchFilter,
    onFilterChange,
    sortMode,
    onSortModeChange,
    hasMore,
    loading,
    loadError,
    searching,
    searchError,
    retrySearch,
    loadMore: loadMoreWorkflows,
  } = state
  const workflowFilterRef = useRef<HTMLInputElement>(null)

  const pinnedIds = useMemo(() => new Set(initialPinnedIds), [initialPinnedIds])

  // The server owns ordering, including where pinned workflows sit, so the list is rendered as
  // returned. Partitioning it client-side would only reorder the loaded page.
  const selectedWorkflowPath =
    selection.type === 'workflow'
      ? workflowFilePathFromRoute(selection.filename, {lab: searchParams.get('lab') === 'true'})
      : undefined

  const selectedWorkflowIsRendered =
    selectedWorkflowPath !== undefined &&
    workflows.some(workflow => workflowRouteMatchesPath(selectedWorkflowPath, workflow))

  const selectionKey = selection.type === 'workflow' ? selection.filename : undefined

  const workflowCountRef = useRef(workflows.length)

  useEffect(() => {
    workflowCountRef.current = workflows.length
  }, [workflows])

  useEffect(() => {
    selectedWorkflowRef.current?.scrollIntoView({block: 'nearest'})
  }, [selectionKey])

  const {pinWorkflow, unpinWorkflow} = useWorkflowPinning({owner, repo, refreshWorkflows})

  const handleLoadMore = useCallback(
    async (trigger: HTMLButtonElement) => {
      if (loading) return {hadError: false, skipped: true}
      track('workflow_sidebar.load_more', 'ref_loc:actions_workflow_sidebar')
      const shouldRestoreFinalPageFocus = document.activeElement === trigger
      const {loadedFinalPage, hadError, skipped} = await loadMoreWorkflows(trigger)
      if (loadedFinalPage && shouldRestoreFinalPageFocus && document.hasFocus() && document.activeElement === trigger) {
        const focusTarget =
          workflowFilterRef.current ??
          document.getElementById(showOnlyRequiredWorkflows ? firstWorkflowLinkId : allWorkflowsLinkId)
        focusTarget?.focus()
      }
      return {hadError, skipped}
    },
    [allWorkflowsLinkId, firstWorkflowLinkId, loading, loadMoreWorkflows, showOnlyRequiredWorkflows, track],
  )
  const Search = Sidebar.Search
  const workflowHeadingActions = searchAndSortEnabled ? (
    <Search
      ref={workflowFilterRef}
      value={searchFilter}
      onChange={value => {
        onFilterChange(value, workflowFilterRef.current ?? undefined)
      }}
      onClear={() => track('workflow_sidebar.clear_search', 'ref_loc:actions_workflow_sidebar')}
      placeholder="Filter workflows..."
      aria-label="Search workflows"
      targetedAction={
        <TargetedActions
          sortMode={sortMode}
          onSortModeChange={mode => {
            track('workflow_sidebar.sort', `ref_loc:actions_workflow_sidebar;sort:${mode}`)
            onSortModeChange(mode)
          }}
        />
      }
    />
  ) : undefined
  const loadMoreLabel = loading ? 'Loading workflows…' : loadError ? 'Try loading workflows again' : 'Show more'

  return (
    <>
      <CollapsibleNavList aria-label="Actions workflows">
        {!showOnlyRequiredWorkflows && (
          <SidebarNavItem
            analyticsKey="all-workflows"
            id={allWorkflowsLinkId}
            label="All workflows"
            href={allWorkflowsHref}
            icon={HomeIcon}
            isCurrent={selection.type === 'all-workflows'}
            softNavigationEnabled={softNavigationEnabled}
          />
        )}

        <CollapsibleNavList.Group
          title="Workflows"
          icon={isExpanded ? undefined : WorkflowIcon}
          expandedHeading={searchAndSortEnabled}
          headingLevel="h3"
          popoverWidth="medium"
          search={workflowHeadingActions}
        >
          {selection.type === 'workflow' && !selectedWorkflowIsRendered && (
            <CollapsibleNavList.Item aria-current="page" className="d-none">
              {selection.filename}
            </CollapsibleNavList.Item>
          )}
          {workflows.map((workflow, index) => {
            const isSelectedWorkflow =
              selectedWorkflowPath !== undefined && workflowRouteMatchesPath(selectedWorkflowPath, workflow)

            return (
              <WorkflowNavItem
                key={workflow.id}
                id={index === 0 ? firstWorkflowLinkId : undefined}
                ref={isSelectedWorkflow ? selectedWorkflowRef : undefined}
                workflow={workflow}
                owner={owner}
                repo={repo}
                queryString={qs}
                aria-current={isSelectedWorkflow ? 'page' : undefined}
                isPinned={pinnedIds.has(workflow.id)}
                allowPinning={allowPinning}
                softNavigationEnabled={softNavigationEnabled}
                onPin={pinWorkflow}
                onUnpin={unpinWorkflow}
              />
            )
          })}
          {/*
            Previous results stay on screen while the next request runs, so the pending and error
            status has to render alongside them. Otherwise stale rows read as results for the
            current search or sort.
          */}
          {searching && (
            <li className={styles.LoadingStatus}>
              <Spinner size="small" srText="Loading workflows…" data-testid="workflow-loading-spinner" />
            </li>
          )}
          {!searching && searchError && (
            <li className="color-fg-muted text-small px-3 py-2">Could not load workflows</li>
          )}
          {!searching && !searchError && workflows.length === 0 && (
            <Search.EmptySearchResultsItem>
              {searchFilter ? 'No matching workflows' : 'No workflows found'}
            </Search.EmptySearchResultsItem>
          )}
          {searchError && !searching && (
            <li className="list-style-none px-2">
              <Button type="button" variant="invisible" size="small" block alignContent="start" onClick={retrySearch}>
                <span className="color-fg-muted f6">Try again</span>
              </Button>
            </li>
          )}
          {hasMore && !searchError && (
            <li className="list-style-none px-2">
              <Button
                type="button"
                variant="invisible"
                size="small"
                block
                alignContent="start"
                leadingVisual={
                  loading
                    ? () => <Spinner size="small" srText={null} data-testid="workflow-load-more-spinner" />
                    : undefined
                }
                aria-disabled={loading}
                onClick={event => {
                  event.stopPropagation()
                  const button = event.currentTarget

                  void (async () => {
                    const {hadError, skipped} = await handleLoadMore(button)
                    // Wait for React to commit and paint the newly loaded items (which push
                    // the button down and update the displayed count) before measuring/announcing.
                    requestAnimationFrame(() => {
                      button.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                      })
                      if (!hadError && !skipped) {
                        const count = workflowCountRef.current
                        announce(count === 1 ? '1 workflow shown' : `${count} workflows shown`, {element: button})
                      }
                    })
                  })()
                }}
              >
                <span className="color-fg-muted f6">{loadMoreLabel}</span>
              </Button>
            </li>
          )}
        </CollapsibleNavList.Group>
      </CollapsibleNavList>
    </>
  )
}

interface WorkflowHeadingActionsProps {
  sortMode: WorkflowSidebarState['sortMode']
  onSortModeChange: WorkflowSidebarState['onSortModeChange']
}

const TargetedActions = asSlot(function TargetedActionsComponent({
  sortMode,
  onSortModeChange,
}: WorkflowHeadingActionsProps) {
  const track = useActionsAnalytics()
  return (
    <ActionMenu
      onOpenChange={open => {
        if (open) track('workflow_sidebar.open_sort', 'ref_loc:actions_workflow_sidebar')
      }}
    >
      <ActionMenu.Anchor>
        <IconButton type="button" aria-label="Sort workflows" icon={SortDescIcon} variant="invisible" size="small" />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          <ActionList.Item selected={sortMode === 'default'} onSelect={() => onSortModeChange('default')}>
            Default
          </ActionList.Item>
          <ActionList.Item selected={sortMode === 'name-ascending'} onSelect={() => onSortModeChange('name-ascending')}>
            Name (A–Z)
          </ActionList.Item>
          <ActionList.Item
            selected={sortMode === 'name-descending'}
            onSelect={() => onSortModeChange('name-descending')}
          >
            Name (Z–A)
          </ActionList.Item>
          <ActionList.Item
            selected={sortMode === 'last-run-descending'}
            onSelect={() => onSortModeChange('last-run-descending')}
          >
            Last run (Newest first)
          </ActionList.Item>
          <ActionList.Item
            selected={sortMode === 'last-run-ascending'}
            onSelect={() => onSortModeChange('last-run-ascending')}
          >
            Last run (Oldest first)
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}, ActionList.GroupHeading.TrailingAction)

interface WorkflowNavItemProps {
  id?: string
  ref?: Ref<HTMLAnchorElement>
  workflow: Workflow
  owner: string
  repo: string
  queryString: string
  'aria-current'?: 'page'
  isPinned: boolean
  allowPinning: boolean
  softNavigationEnabled: boolean
  onPin: (workflowId: number) => void
  onUnpin: (workflowId: number) => void
}

const WorkflowNavItem = memo(function WorkflowNavItemComponent({
  id,
  ref,
  workflow,
  owner,
  repo,
  queryString,
  'aria-current': ariaCurrent,
  isPinned,
  allowPinning,
  softNavigationEnabled,
  onPin,
  onUnpin,
}: WorkflowNavItemProps) {
  const track = useActionsAnalytics()
  const href = workflowRouteHref(owner, repo, workflow, queryString)
  const displayName = workflowDisplayName(workflow)
  const {closeDialog} = useSidebarPageLayoutActions()
  const isDisabled = workflow.state !== 'active'
  const isLab = isLabWorkflowPath(workflow.path)
  const linkProps = softNavigationEnabled
    ? ({as: Link, to: href} as unknown as ComponentProps<typeof CollapsibleNavList.Item>)
    : {href}

  return (
    <CollapsibleNavList.Item
      id={id}
      ref={ref}
      {...linkProps}
      aria-current={ariaCurrent}
      className={styles.navItem}
      onClick={event => {
        if ((event.target as HTMLElement).closest('button')) return
        track('workflow_sidebar.select_workflow', 'ref_loc:actions_workflow_sidebar')
        closeDialog()
      }}
    >
      {displayName}
      {(isDisabled || isLab) && (
        <CollapsibleNavList.TrailingVisual>
          <span className="d-inline-flex flex-items-center gap-1">
            {isLab && (
              <Label variant="secondary" size="small">
                Lab
              </Label>
            )}
            {isDisabled && <span className="color-fg-muted text-small">Disabled</span>}
          </span>
        </CollapsibleNavList.TrailingVisual>
      )}
      {allowPinning && !isDisabled && (
        <CollapsibleNavList.TrailingAction
          as="button"
          icon={isPinned ? PinSlashIcon : PinIcon}
          label={isPinned ? `Unpin ${displayName}` : `Pin ${displayName}`}
          className={isPinned ? styles.pinnedIcon : styles.pinButton}
          onClick={event => {
            event.stopPropagation()
            track(isPinned ? 'workflow_sidebar.unpin' : 'workflow_sidebar.pin', 'ref_loc:actions_workflow_sidebar')
            if (isPinned) {
              onUnpin(workflow.id)
            } else {
              onPin(workflow.id)
            }
          }}
        />
      )}
    </CollapsibleNavList.Item>
  )
})
